import React, { memo, useMemo } from "react";
import type { SliderSingleProps } from "antd/lib";
import { Button, Divider, Skeleton, Typography } from "antd/lib";
import { Slider } from "antd";
import MyCard from "@/app/components/MyCard";
import { useTranslations } from "next-intl";
import type { IProductDetail } from "@/models/product";
import { FireOutlined } from "@ant-design/icons";
import DisplayChartUp from "@/svg/DisplayChartUp";
import ChartColumn from "@/svg/ChartColumn";
import { formatPercent, formatThousands } from "@/helpers";
import StatusGauge from "@/app/components/charts/statusGauge";
import Bearish from "@/svg/Bearish";
import Bull from "@/svg/Bull";
import { useCurrencyContext } from "@/context/CurrencyContext";

function normalizeSliderValue(value: number, low: number, high: number) {
  return ((value - low) / (high - low)) * 100;
}

const TokenHealth = ({ productDetail, isLoading }: { productDetail?: IProductDetail; isLoading: boolean }) => {
  const t = useTranslations("products");
  const { currentCurrency } = useCurrencyContext();

  const { low, high, close } = productDetail?.forecasting_analysis || { low: 0, high: 0, close: 0 };

  const score = useMemo(
    () => productDetail?.klarda_score?.find(item => item.name === "fundamental_score")?.score || 0,
    [productDetail?.klarda_score],
  );

  const fillArrowDownDex = useMemo(() => {
    switch (true) {
      case score >= 0 && score <= 45:
        return "#FC783E";
      case score > 45 && score <= 75:
        return "#F5D417";
      case score > 75 && score <= 100:
        return "#7ECA19";
    }
  }, [score]);

  const fillText = useMemo(() => {
    switch (true) {
      case score >= 0 && score <= 20:
        return "Poor";
      case score > 20 && score <= 40:
        return "Bad";
      case score > 40 && score <= 60:
        return "Neutral";
      case score > 60 && score <= 80:
        return "Good";
      case score > 80 && score <= 100:
        return "Good";
    }
  }, [score]);

  const exchangeHealthData = useMemo(
    () => [
      {
        name: "Net Flow (24h)",
        background: "#FA8C16",
        value: `${formatThousands(productDetail?.total_holder)} ${productDetail?.symbol}`,
        icon: <FireOutlined />,
      },
      {
        name: "Day’s Range",
        background: "#1A56DB",
        value: `(${formatThousands(productDetail?.days_range.low, currentCurrency)} - ${formatThousands(
          productDetail?.days_range.high,
          currentCurrency,
        )})`,
        icon: <DisplayChartUp />,
      },
      {
        name: "52-week Range",
        background: "#36CFC9",
        value: `(${formatThousands(productDetail?.weeks52_range.low, currentCurrency)} - ${formatThousands(
          productDetail?.weeks52_range.high,
          currentCurrency,
        )})`,
        icon: <ChartColumn />,
      },
    ],
    [
      currentCurrency,
      productDetail?.days_range.high,
      productDetail?.days_range.low,
      productDetail?.symbol,
      productDetail?.total_holder,
      productDetail?.weeks52_range.high,
      productDetail?.weeks52_range.low,
    ],
  );

  const marks: SliderSingleProps["marks"] = useMemo(
    () => ({
      [normalizeSliderValue(low, low, high)]: (
        <div className="flex flex-col">
          <Typography.Text strong>{formatThousands(low)}</Typography.Text>
          <Typography.Text>Low</Typography.Text>
        </div>
      ),
      [normalizeSliderValue(close, low, high)]: (
        <div className="flex flex-col">
          <Typography.Text strong>{formatThousands(close)}</Typography.Text>
          <Typography.Text>Current</Typography.Text>
        </div>
      ),
      [normalizeSliderValue(high, low, high)]: (
        <div className="flex flex-col w-max mr-4">
          <Typography.Text strong>{formatThousands(high, "", true)}</Typography.Text>
          <Typography.Text>High</Typography.Text>
        </div>
      ),
    }),
    [close, high, low],
  );

  return (
    <MyCard title={t("Token_health")} styles={{ body: { padding: 0 } }}>
      {isLoading ? (
        <div className="mt-2">
          <Skeleton paragraph={{ rows: 10 }} />
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          <>
            <div className={"h-[15px] mt-3"} style={{ marginLeft: `${score}%` }}>
              <ArrowDownIco fill={fillArrowDownDex} />
            </div>
            <div className="flex flex-col gap-1">
              <div
                className="w-full h-4 rounded-lg"
                style={{ background: "linear-gradient(90deg, #FC783E 0%, #F5D417 47.92%, #7ECA19 96.35%)" }}
              />
              <Typography.Title
                level={5}
                style={{ color: fillArrowDownDex, marginLeft: `${score > 90 ? score - 8 : score}%` }}
                className="mb-0"
              >
                {fillText}
              </Typography.Title>
            </div>
          </>
          <Divider className="my-2 w-full" />
          {/*Exchange_Health*/}
          <div className="flex flex-col gap-2">
            <Typography.Title level={5} className="m-0">
              {t("Exchange_Health")}
            </Typography.Title>
            {exchangeHealthData?.map(item => (
              <div key={item.name} className="flex justify-between items-center">
                <div className="flex gap-2 items-center">
                  <Button
                    size="large"
                    icon={item.icon}
                    styles={{ icon: { color: "#ffffff" } }}
                    style={{ background: item.background }}
                  />
                  <Typography.Text>{item.name}</Typography.Text>
                </div>
                <Typography.Text>{item.value}</Typography.Text>
              </div>
            ))}
          </div>
          <Divider className="my-2 w-full" />
          {/*Technical_Analysis*/}
          <div className="flex flex-wrap justify-between items-center">
            <div className="flex flex-col gap-1">
              <Typography.Title level={5} className="m-0">
                {t("Technical_Analysis")}
              </Typography.Title>
              <Typography.Text
                strong
                type={productDetail?.technical_summary?.statistic === "Sell" ? "success" : "danger"}
              >
                {productDetail?.technical_summary?.statistic}
              </Typography.Text>
            </div>
            <StatusGauge
              className="lg:max-w-[200px] lg:max-h-[100px]"
              content={productDetail?.technical_summary?.statistic}
              score={(productDetail?.technical_summary?.value as number) * 10 || 0}
              max={100}
            />
          </div>
          {/*Forecasting_Analysis*/}
          <Divider className="my-2 w-full" />
          <div className="flex justify-between flex-col xl:flex-row gap-2">
            <div className="flex flex-col gap-1 items-center mt-4">
              <Typography.Title level={5} className="m-0">
                {t("Forecasting_Analysis")}
              </Typography.Title>
            </div>
            <Slider className="w-[200px] xl:mr-4 m-auto mb-8 xl:m-0 min-w-[220px]" marks={marks} defaultValue={close} />
          </div>
          {/*Community_Analysis*/}
          <Divider className="w-full mb-2" />
          <div className="flex flex-col gap-2">
            <Typography.Title level={5}>{t("Community_Analysis")}</Typography.Title>
            <div className="flex gap-4 m-auto">
              <Button type="default">
                <div className="flex items-center gap-1 cursor-default">
                  <Bearish /> {formatPercent(productDetail?.community_analysis?.bearish)}% Bearish
                </div>
              </Button>
              <Button type="default">
                <div className="flex items-center gap-1 cursor-default">
                  <Bull /> {formatPercent(productDetail?.community_analysis?.bullish)}% Bullish
                </div>
              </Button>
            </div>
          </div>
        </div>
      )}
    </MyCard>
  );
};

export default memo(TokenHealth);

const ArrowDownIco = ({ fill = "#000" }: { fill?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    fill={fill}
    version="1.1"
    viewBox="0 0 96.154 96.154"
    xmlSpace="preserve"
  >
    <path d="M.561 20.971l45.951 57.605c.76.951 2.367.951 3.127 0l45.956-57.609c.547-.689.709-1.716.414-2.61a2.686 2.686 0 00-.186-.437 2.004 2.004 0 00-1.765-1.056H2.093c-.736 0-1.414.405-1.762 1.056a2.62 2.62 0 00-.184.426c-.297.905-.136 1.934.414 2.625z" />
  </svg>
);
